import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, User, LayoutDashboard, Crown, MessageCircle, ShoppingBag, Heart, Shield, HelpCircle, FileText, Upload, Settings, Gift, DollarSign, Users, Megaphone, Search, Trophy, HandCoins, BarChart3 } from "lucide-react";
import logo from "@/assets/logo.webp";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { transformStorageImage } from "@/lib/imageTransform";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const centerLinks = [
  { label: "Browse Notes", href: "/browse" },
  { label: "Sell & Earn", href: "/sell", highlight: true },
  { label: "Notes Hub", href: "/notes" },
  { label: "AI Tools", href: "/ai-tools" },
  { label: "AI Tutor", href: "/ai-tutor" },
  { label: "Leaderboard", href: "/leaderboard" },
  { label: "Study Groups", href: "/my-groups" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, loading, subscription } = useAuth();
  const { isAdmin } = useIsAdmin();
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Fetch display name + avatar from profiles table
  useEffect(() => {
    if (!user) { setDisplayName(null); setAvatarUrl(null); return; }
    const metaAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture || null;
    supabase
      .from("public_profiles")
      .select("display_name, avatar_url")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        setDisplayName(
          data?.display_name ||
          user.user_metadata?.full_name ||
          user.user_metadata?.name ||
          user.email?.split("@")[0] ||
          "User"
        );
        setAvatarUrl(data?.avatar_url || metaAvatar);
      });
  }, [user]);

  const userName = displayName || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split("@")[0] || "User";
  const userInitial = userName.charAt(0).toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
      <div className="container mx-auto flex h-14 items-center px-4 gap-4">
        {/* Left — Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img src={logo} alt="GatorNotes official logo" width={32} height={32} decoding="async" className="h-8 w-8" />
          <span className="font-display text-lg font-bold text-foreground hidden sm:inline">GatorNotes</span>
        </Link>

        {/* Center — Nav links */}
        <div className="hidden md:flex items-center gap-0.5 flex-1 justify-center">
          {centerLinks.map((link) => {
            const isActive = location.pathname === link.href;
            const baseCls = "rounded-md px-2 lg:px-3 py-1.5 text-[12px] lg:text-[13px] font-semibold transition-all whitespace-nowrap inline-flex items-center gap-1";
            const cls = link.highlight
              ? isActive
                ? `${baseCls} bg-accent/15 text-accent ring-1 ring-accent/30`
                : `${baseCls} text-accent hover:bg-accent/10 ring-1 ring-accent/20`
              : isActive
                ? `${baseCls} bg-primary/10 text-primary`
                : `${baseCls} text-muted-foreground hover:bg-muted hover:text-foreground font-medium`;
            return (
              <Link key={link.href} to={link.href} className={cls}>
                {link.highlight && <DollarSign className="h-3 w-3" />}
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Right — Upload + Profile */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <Button
            variant="hero"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => navigate(user ? "/upload" : "/sell")}
          >
            <Upload className="h-3.5 w-3.5" />
            {user ? "Upload" : "Start Earning"}
          </Button>

          {!loading && (
            user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 h-8 pl-1 pr-2.5">
                    {avatarUrl ? (
                      <img
                        src={transformStorageImage(avatarUrl, { width: 48, height: 48, quality: 75 }) || avatarUrl}
                        alt={`Profile picture of ${userName}`}
                        width={24}
                        height={24}
                        decoding="async"
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold">
                        {userInitial}
                      </div>
                    )}
                    <span className="max-w-[120px] truncate text-xs">
                      {userName}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-foreground">{userName}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    My Notes
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/my-purchases")}>
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    Purchases
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/my-offers")}>
                    <HandCoins className="h-4 w-4 mr-2" />
                    My Offers
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/seller/offers")}>
                    <HandCoins className="h-4 w-4 mr-2" />
                    Offers Received
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wishlist")}>
                    <Heart className="h-4 w-4 mr-2" />
                    Wishlist
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")}>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Messages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/referrals")}>
                    <Gift className="h-4 w-4 mr-2" />
                    Referrals
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/challenge")}>
                    <Trophy className="h-4 w-4 mr-2" />
                    Creator Challenge
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/plus")}>
                    <Crown className="h-4 w-4 mr-2" />
                    {subscription.isPlus ? "Plus Membership" : "Upgrade to Plus"}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin/users")}>
                        <Users className="h-4 w-4 mr-2" />
                        Admin Users
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/analytics")}>
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Marketplace Analytics
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/payouts")}>
                        <DollarSign className="h-4 w-4 mr-2" />
                        Admin Payouts
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/inquiries")}>
                        <HelpCircle className="h-4 w-4 mr-2" />
                        Admin Inquiries
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/notes")}>
                        <FileText className="h-4 w-4 mr-2" />
                        Note Review
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/promotions")}>
                        <Megaphone className="h-4 w-4 mr-2" />
                        Admin Promotions
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/seo")}>
                        <Search className="h-4 w-4 mr-2" />
                        Admin SEO
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate("/auth")}>
                  Log in
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate("/auth?mode=signup")}>
                  Sign up
                </Button>
              </>
            )
          )}
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center gap-2 md:hidden ml-auto">
          <Button
            variant="hero"
            size="sm"
            className="h-8 text-xs gap-1"
            onClick={() => navigate(user ? "/upload" : "/sell")}
          >
            <Upload className="h-3.5 w-3.5" />
            {user ? "Upload" : "Earn"}
          </Button>
          <button
            type="button"
            className="text-foreground p-1"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-border md:hidden"
          >
            <div className="flex flex-col gap-1 p-4 max-h-[calc(100vh-3.5rem)] overflow-y-auto">
              {centerLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                    location.pathname === link.href
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
                {user ? (
                  <>
                    <div className="px-4 py-2">
                      <p className="text-sm font-medium text-foreground">{userName}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/profile"); }}>
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/dashboard"); }}>
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      My Notes
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/my-purchases"); }}>
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      Purchases
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/wishlist"); }}>
                      <Heart className="h-4 w-4 mr-2" />
                      Wishlist
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/messages"); }}>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Messages
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/referrals"); }}>
                      <Gift className="h-4 w-4 mr-2" />
                      Referrals
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/challenge"); }}>
                      <Trophy className="h-4 w-4 mr-2" />
                      Creator Challenge
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/plus"); }}>
                      <Crown className="h-4 w-4 mr-2" />
                      {subscription.isPlus ? "Plus Membership" : "Upgrade to Plus"}
                    </Button>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/admin/users"); }}>
                          <Users className="h-4 w-4 mr-2" />
                          Admin Users
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/admin/analytics"); }}>
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Marketplace Analytics
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/admin/payouts"); }}>
                          <DollarSign className="h-4 w-4 mr-2" />
                          Admin Payouts
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/admin/inquiries"); }}>
                          <HelpCircle className="h-4 w-4 mr-2" />
                          Admin Inquiries
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/admin/notes"); }}>
                          <FileText className="h-4 w-4 mr-2" />
                          Note Review
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/admin/promotions"); }}>
                          <Megaphone className="h-4 w-4 mr-2" />
                          Admin Promotions
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/admin/seo"); }}>
                          <Search className="h-4 w-4 mr-2" />
                          Admin SEO
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      Log out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => { setMobileOpen(false); navigate("/auth"); }}>
                      Log in
                    </Button>
                    <Button variant="hero" size="sm" onClick={() => { setMobileOpen(false); navigate("/auth?mode=signup"); }}>
                      Sign up free
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
